import { makeStyles } from '@material-ui/core/styles'
import { chatDrawerSelectors } from 'ducks/chatDrawer'
import { wsSelectors } from 'ducks/websocket'
import React, { useEffect, useState } from 'react'
import Markdown from 'react-markdown'
import { useSelector } from 'react-redux'
const SLIDE_OUT_DURATION = 50 // ms
const SLIDE_IN_DURATION = 100 // ms
const SHIMMER_DURATION = 3000 // ms

const useStyles = makeStyles((theme: any) => ({
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '8px',
    alignSelf: 'flex-start',
    overflow: 'visible',
    minHeight: '20px',
  },
  textContainer: {
    position: 'relative',
    minHeight: '20px', // Single line height
    maxHeight: '40px', // Max 2 lines * 20px line height
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
  },
  shimmerText: {
    background: 'linear-gradient(90deg, #9ca3af 25%, #374151 50%, #9ca3af 75%)',
    backgroundSize: '200% 100%',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
    fontWeight: 500,
    lineHeight: '20px',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    maxHeight: '40px', // Ensure it doesn't exceed 2 lines
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden', // Required for line-clamp, but positioned to not clip top
  },
  slideInUp: {
    animation: `$slideInUp ${SLIDE_IN_DURATION}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) both, $shimmer ${SHIMMER_DURATION}ms ease-in-out infinite`,
  },
  slideOutUp: {
    animation: `$slideOutUp ${SLIDE_OUT_DURATION}ms cubic-bezier(0.55, 0.055, 0.675, 0.19) both, $shimmer ${SHIMMER_DURATION}ms ease-in-out infinite`,
  },
  '@keyframes shimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(20px)',
      opacity: 0,
    },
    '25%': {
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
  },
  '@keyframes slideOutUp': {
    '0%': {
      transform: 'translateY(0)',
      opacity: 1,
    },
    '100%': {
      transform: 'translateY(-20px)',
      opacity: 0,
    },
  },
}))

interface AdaProgressiveLoaderProps {}

const AdaProgressiveLoader: React.FC<AdaProgressiveLoaderProps> = () => {
  const classes = useStyles()
  let adaStatus = useSelector(chatDrawerSelectors.getAdaStatus)
  const websocketAvailable = useSelector(wsSelectors.getAvailable)
  const websocketState = useSelector(wsSelectors.getState)
  const [currentContent, setCurrentContent] = useState('Ada is thinking...')
  const [isSlideOut, setIsSlideOut] = useState(false)

  const newContent = adaStatus || 'Ada is thinking...'

  adaStatus = 'Ada is thinking...'

  useEffect(() => {
    if (newContent !== currentContent) {
      setIsSlideOut(true)

      const timer = setTimeout(() => {
        setCurrentContent(newContent)
        setIsSlideOut(false)
      }, SLIDE_OUT_DURATION)

      return () => clearTimeout(timer)
    }
  }, [newContent, currentContent])

  if (!websocketAvailable || websocketState !== 'connected' || !adaStatus) {
    return null
  }

  const processedContent = currentContent.replace(/\n+/g, ' ').trim()

  // Render markdown elements as inline spans to preserve shimmer effect
  const inlineSpan = (props: any) => {
    const { node, ...rest } = props
    return <span {...rest} />
  }

  const markdownComponents = {
    // Block elements rendered as inline spans
    p: inlineSpan,
    h1: inlineSpan,
    h2: inlineSpan,
    h3: inlineSpan,
    h4: inlineSpan,
    h5: inlineSpan,
    h6: inlineSpan,
    ul: inlineSpan,
    ol: inlineSpan,
    li: inlineSpan,
    code: inlineSpan,
    pre: inlineSpan,
    blockquote: inlineSpan,
    a: inlineSpan,
    // Inline elements preserved
    strong: (props: any) => <strong {...props} />,
    em: (props: any) => <em {...props} />,
  }

  return (
    <div className={classes.loaderContainer}>
      <div className={classes.textContainer}>
        <div className={`${classes.shimmerText} ${isSlideOut ? classes.slideOutUp : classes.slideInUp}`}>
          <Markdown components={markdownComponents}>{processedContent}</Markdown>
        </div>
      </div>
    </div>
  )
}

export default AdaProgressiveLoader
